import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { appendPurchaseListFilters, PurchaseListFilters } from '../po/po.service';

/** One post-posting amendment, as returned by GET /grn/:id/revisions. */
export interface GrnRevision {
  id: string;
  revisionNo: number;
  statusAtEdit: string;
  reasonNote: string | null;
  reason: {
    code: string | null;
    shortReason: string | null;
    description: string | null;
  } | null;
  changeSummary: string | null;
  /** Full header + lines on each side of the edit. */
  beforeSnapshot: any;
  afterSnapshot: any;
  /** [{ variantId, batchNumber, stockQty, ledgerQty }] — what actually moved. */
  stockDeltas: any[] | null;
  editedByUser: { id: string; name: string; username: string } | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface GrnRevisionHistory {
  grnNumber: string;
  revisionNo: number;
  revisions: GrnRevision[];
}

interface PurchaseTaxReportPayload {
  preset?: string;
  fromDate?: string;
  toDate?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GrnService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  createGrn(body: any) {
    return this.http.post(`${this.baseUrl}/grn`, body);
  }

  /**
   * Cancel a posted GRN. The server reverses the whole receipt out of stock,
   * marks the document CANCELLED and records the reason on its amendment
   * trail — and refuses when the stock has already moved on.
   */
  cancelGrn(id: string, body: { reasonId: string; reasonNote?: string }) {
    return this.http.post(`${this.baseUrl}/grn/${id}/cancel`, body);
  }

  getGrns(
    pageOptions: { page: number; limit: number },
    searchText: string,
    filters?: PurchaseListFilters,
  ) {
    let params = new HttpParams()
      .set('page', pageOptions.page)
      .set('limit', pageOptions.limit);
    params = appendPurchaseListFilters(params, filters);
    return this.http.get(`${this.baseUrl}/grn`, { params });
  }

  updateGrn(id: string, body: any) {
    return this.http.patch(`${this.baseUrl}/grn/${id}`, body);
  }

  getGrnById(id: string) {
    return this.http.get(`${this.baseUrl}/grn/${id}`);
  }

  /**
   * Amendment history: every edit made to this GRN after it posted, newest
   * first, with the before/after snapshots. Read-only — the trail is
   * append-only server-side.
   */
  getGrnRevisions(id: string): Observable<GrnRevisionHistory> {
    return this.http.get<GrnRevisionHistory>(
      `${this.baseUrl}/grn/${id}/revisions`,
    );
  }

  downloadGrn(grnId: string) {
    return this.http.get(`${this.baseUrl}/grn/download/${grnId}`, {
      responseType: 'blob' as 'blob',
      observe: 'response' as 'response',
    });
  }

  getNextGrnNumber(storeId?: string) {
    let params = new HttpParams();
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get(`${this.baseUrl}/grn/next-number`, { params });
  }

  searchByGrnNumber(grnNumber: string, filters?: PurchaseListFilters) {
    let params = new HttpParams().set('grnNumber', grnNumber);
    params = appendPurchaseListFilters(params, filters);
    return this.http.get(`${this.baseUrl}/grn/search-by-grn`, { params });
  }

  getPostedGrns() {
    return this.http.get(`${this.baseUrl}/grn/status/posted`);
  }
  exportPurchaseTaxReport(payload: PurchaseTaxReportPayload): Observable<Blob> {
    const url = `${this.baseUrl}/grn/reports/purchase-tax`;

    // Use POST to send the date criteria in the body
    return this.http.post(url, payload, {
      // responseType must be 'blob' for CSV/file download
      responseType: 'blob',
    });
  }

  downloadVendorReceipt(grnId: string, storeId: string) {
    return this.http.get(
      `${this.baseUrl}/grn/vendor-receipt/${grnId}/${storeId}`,
      {
        responseType: 'blob' as 'blob',
        observe: 'response' as 'response',
      }
    );
  }

  getGinsWithoutGrn() {
    return this.http.get(`${this.baseUrl}/gin/without-grn`);
  }

  getGinById(ginId: string) {
    return this.http.get(`${this.baseUrl}/gin/${ginId}`);
  }

  getGinPendingItems(ginId: string) {
    return this.http.get(`${this.baseUrl}/gin/${ginId}/pending-items`);
  }

  // Close a partially-received GIN so it stops appearing in the GRN dropdown
  // (user confirmed the remaining ginned qty will not be received).
  closeGin(ginId: string) {
    return this.http.patch(`${this.baseUrl}/gin/${ginId}/close`, {});
  }
}
